import { useCallback, useContext, useMemo, useState } from 'react';

import { TurnstileInstance } from '@frontend/api/types';
import { ConnectionContext } from '@frontend/contexts';

import { UserInstruction } from '../types';

export default function useProductScopeSettings({
  userInstruction,
  onUpdate,
}: {
  userInstruction: UserInstruction;
  onUpdate: (userInstruction: UserInstruction) => void;
}) {
  const { products } = useContext(ConnectionContext);

  const [addProductScope, setAddProductScope] = useState(false);
  const [newProductInstance, setNewProductInstance] = useState<{
    product_name: string;
    instance?: TurnstileInstance;
  } | null>(null);

  const productsToRender = useMemo(() => {
    const productsToRender = [];
    for (const product_sku of userInstruction.product_sku) {
      const product_name = Object.keys(products).find(
        product_name => products[product_name].scope_name === product_sku
      );
      if (product_name) {
        productsToRender.push({ product_name, product_sku });
      }
    }
    return productsToRender;
  }, [products, userInstruction.product_sku]);

  const productInstancesToRender = useMemo(() => {
    const allInstances = Object.values(products).flatMap(product => product.instances);
    const selectedInstances = [];
    for (const id of userInstruction.instance_id) {
      const instance = allInstances.find(instance => instance.id === id);
      if (instance && !userInstruction.product_sku.includes(instance.product_sku)) {
        selectedInstances.push(instance);
      }
    }
    return selectedInstances;
  }, [products, userInstruction.instance_id, userInstruction.product_sku]);

  const selectableProductNames = useMemo(
    () =>
      Object.keys(products).filter(
        product_name =>
          !userInstruction.product_sku.includes(products[product_name].scope_name) &&
          products[product_name].instances.some(
            instance => !userInstruction.instance_id.includes(instance.id) && !instance.disabled
          )
      ),
    [products, userInstruction.instance_id, userInstruction.product_sku]
  );

  const selectableProductInstances = useMemo(() => {
    if (!newProductInstance?.product_name) return [];

    return products[newProductInstance.product_name].instances.filter(
      instance => !userInstruction.instance_id.includes(instance.id) && !instance.disabled
    );
  }, [newProductInstance?.product_name, products, userInstruction.instance_id]);

  const handleAddProductInstance = useCallback(
    (instance: TurnstileInstance) => {
      const newInstanceIds = [...userInstruction.instance_id];
      if (!newInstanceIds.includes(instance.id)) {
        newInstanceIds.push(instance.id);
      }

      onUpdate({
        ...userInstruction,
        product_sku: userInstruction.product_sku.filter(sku => sku !== instance.product_sku),
        instance_id: newInstanceIds,
      });
      setAddProductScope(false);
      setNewProductInstance(null);
    },
    [onUpdate, userInstruction]
  );

  const handleRemoveProductInstance = useCallback(
    (instanceId: string) => {
      onUpdate({
        ...userInstruction,
        instance_id: userInstruction.instance_id.filter(id => id !== instanceId),
      });
    },
    [onUpdate, userInstruction]
  );

  const handleAddAllProductInstances = useCallback(
    (product_name: string) => {
      const filteredInstanceIds = userInstruction.instance_id.filter(
        id => !products[product_name].instances.some(instance => instance.id === id)
      );

      const newProductSkus = [...userInstruction.product_sku];
      const newSku = products[product_name].scope_name;
      if (!newProductSkus.includes(newSku)) {
        newProductSkus.push(newSku);
      }

      onUpdate({
        ...userInstruction,
        instance_id: filteredInstanceIds,
        product_sku: newProductSkus,
      });
      setAddProductScope(false);
      setNewProductInstance(null);
    },
    [onUpdate, products, userInstruction]
  );

  const handleRemoveProduct = useCallback(
    (product_sku: string) => {
      onUpdate({
        ...userInstruction,
        product_sku: userInstruction.product_sku.filter(sku => sku !== product_sku),
      });
    },
    [onUpdate, userInstruction]
  );

  return {
    products,
    productsToRender,
    productInstancesToRender,
    selectableProductNames,
    selectableProductInstances,
    addProductScope,
    setAddProductScope,
    newProductInstance,
    setNewProductInstance,
    handleAddProductInstance,
    handleRemoveProductInstance,
    handleAddAllProductInstances,
    handleRemoveProduct,
  };
}
